import imageCompression from 'browser-image-compression';
import {
  ALLOWED_IMAGE_TYPES,
  MAX_FILE_SIZE_KB,
  getFileExtension,
  getFileTypeError,
  uploadToS3WithRetry,
} from './imageUtils';

/**
 * Strategy image dimensions.
 * Target: 764x544 (2x of 382x272 display size).
 * Aspect ratio: 382/272 ~ 1.404
 */
export const STRATEGY_IMAGE_DIMENSIONS = {
  width: 764,
  height: 544,
  aspectRatio: 382 / 272,
};

/**
 * Crop image to strategy dimensions using canvas.
 */
export async function getCroppedStrategyImage(
  imageSrc: string,
  croppedAreaPixels: { x: number; y: number; width: number; height: number }
): Promise<Blob> {
  const image = await createImage(imageSrc);
  const canvas = document.createElement('canvas');
  const ctx = canvas.getContext('2d');

  if (!ctx) {
    throw new Error('Failed to get canvas context');
  }

  canvas.width = STRATEGY_IMAGE_DIMENSIONS.width;
  canvas.height = STRATEGY_IMAGE_DIMENSIONS.height;

  ctx.drawImage(
    image,
    croppedAreaPixels.x,
    croppedAreaPixels.y,
    croppedAreaPixels.width,
    croppedAreaPixels.height,
    0,
    0,
    STRATEGY_IMAGE_DIMENSIONS.width,
    STRATEGY_IMAGE_DIMENSIONS.height
  );

  return new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) => {
        if (!blob) {
          reject(new Error('Canvas is empty'));
          return;
        }
        resolve(blob);
      },
      'image/jpeg',
      0.95
    );
  });
}

/**
 * Compress strategy image if needed (same threshold as activities).
 */
export async function compressStrategyImageIfNeeded(blob: Blob): Promise<Blob> {
  const fileSizeKB = blob.size / 1024;

  if (fileSizeKB <= MAX_FILE_SIZE_KB) {
    return blob;
  }

  const file = new File([blob], 'image.jpg', { type: 'image/jpeg' });

  const compressedFile = await imageCompression(file, {
    maxSizeMB: MAX_FILE_SIZE_KB / 1024,
    maxWidthOrHeight: STRATEGY_IMAGE_DIMENSIONS.width,
    useWebWorker: true,
    fileType: 'image/jpeg',
  });

  return compressedFile;
}

// Re-export shared utilities
export { ALLOWED_IMAGE_TYPES, MAX_FILE_SIZE_KB, getFileExtension, getFileTypeError, uploadToS3WithRetry };

/**
 * Helper to create an Image element from a source URL.
 */
function createImage(url: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const image = new Image();
    image.addEventListener('load', () => resolve(image));
    image.addEventListener('error', (error) => reject(error));
    image.setAttribute('crossOrigin', 'anonymous');
    image.src = url;
  });
}
